<template>
  <div class="rank-page">
    <header class="page-header">
      <div>
        <p class="kicker">{{ pageKicker }}</p>
        <h1>{{ pageTitle }}</h1>
        <p class="support">{{ pageLead }}</p>
        <p v-if="pane === 'ranked' && seasonLabel" class="season">{{ seasonLabel }}</p>
      </div>
      <router-link to="/" class="text-btn">
        {{ t('rank.back') }}
      </router-link>
    </header>

    <p v-if="activeError" class="empty">
      {{ t('rank.loadError') }}
      <button type="button" class="text-btn retry" @click="onRetry">
        {{ t('rank.retry') }}
      </button>
    </p>

    <div class="pane-tabs" role="tablist" :aria-label="t('rank.title')">
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'ranked' }"
        :aria-selected="pane === 'ranked'"
        @click="setPane('ranked')"
      >
        {{ t('rank.tabs.ranked') }}
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'level' }"
        :aria-selected="pane === 'level'"
        @click="setPane('level')"
      >
        {{ t('rank.tabs.level') }}
      </button>
      <button
        v-for="id in AI_WIN_LADDERS"
        :key="id"
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === id }"
        :aria-selected="pane === id"
        @click="setPane(id)"
      >
        {{ t(`settings.vsAiLevels.${id}`) }}
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'event' }"
        :aria-selected="pane === 'event'"
        @click="setPane('event')"
      >
        {{ t('rank.tabs.event') }}
      </button>
      <button
        type="button"
        role="tab"
        class="pane-tab"
        :class="{ active: pane === 'prizes' }"
        :aria-selected="pane === 'prizes'"
        @click="setPane('prizes')"
      >
        {{ t('rank.tabs.prizes') }}
      </button>
    </div>

    <template v-if="pane === 'ranked'">
    <section class="md-panel hero">
      <div v-if="standing" class="hero-row">
        <RankBadge
          :league="standing.league"
          :division="standing.division"
          :placement-remaining="standing.placementRemaining"
          :size="96"
        />
        <div class="hero-copy">
          <p class="hero-name">{{ standingLabel }}</p>
          <p v-if="standingLp" class="hero-lp">{{ standingLp }}</p>
          <div
            v-if="showLpBar"
            class="lp-bar"
            aria-hidden="true"
          >
            <span class="lp-fill" :style="{ width: lpPct + '%' }" />
          </div>
        </div>
      </div>
      <p v-else-if="!loadError" class="hint">{{ t('rank.loading') }}</p>
      <router-link to="/lobby" class="md-btn-tonal play">
        {{ t('rank.play') }}
      </router-link>
    </section>

    <h2>{{ t('rank.board') }}</h2>
    <p v-if="loading && board.length === 0" class="hint">
      {{ t('rank.loading') }}
    </p>
    <ol v-else-if="board.length > 0 || showYouRow" class="rank-board">
      <li
        v-for="(row, i) in board"
        :key="row.username + i"
        :class="{ me: isMe(row) }"
        :aria-current="isMe(row) ? 'true' : undefined"
      >
        <span class="rank-pos">{{ placeAt('ranked', i) }}</span>
        <AvatarView
          :config="portraitFor(row.username, row.avatar)"
          :size="48"
        />
        <RankBadge
          :league="row.league"
          :division="row.division"
          :placement-remaining="0"
          :size="40"
        />
        <div class="rank-who">
          <router-link
            class="rank-user"
            :to="publicProfilePath(row.username)"
          >{{ row.username }}</router-link>
          <span v-if="isMe(row)" class="you-chip">{{ t('rank.you') }}</span>
        </div>
        <span class="rank-meta">{{ boardLabel(row) }}</span>
      </li>
      <li v-if="showYouRow && youEntry" class="me off-board" aria-current="true">
        <span class="rank-pos">{{ youPosition }}</span>
        <AvatarView :config="player.avatar" :size="48" />
        <RankBadge
          :league="youEntry.league"
          :division="youEntry.division"
          :placement-remaining="youEntry.placementRemaining"
          :size="40"
        />
        <div class="rank-who">
          <router-link
            class="rank-user"
            :to="publicProfilePath(youEntry.username)"
          >{{ youEntry.username }}</router-link>
          <span class="you-chip">{{ t('rank.you') }}</span>
        </div>
        <span class="rank-meta">{{ boardLabel(youEntry) }}</span>
      </li>
    </ol>
    <p v-else-if="!loading && !loadError" class="hint">
      {{ t('rank.boardEmpty') }}
    </p>
    </template>

    <template v-else-if="pane === 'level'">
    <section v-if="levelYou" class="md-panel hero">
      <div class="hero-row">
        <span class="level-badge level-badge-lg">{{ levelYou.level }}</span>
        <div class="hero-copy">
          <p class="hero-name">{{ t('level.chip', { n: levelYou.level }) }}</p>
          <p v-if="levelPlace" class="hero-lp">{{ levelPlace }}</p>
          <div class="lp-bar" aria-hidden="true">
            <span class="lp-fill" :style="{ width: levelPct + '%' }" />
          </div>
          <p class="hero-lp">{{ levelBarText(levelYou) }}</p>
        </div>
      </div>
    </section>
    <p v-else-if="!levelError" class="hint">{{ t('level.loading') }}</p>

    <h2 v-if="!loading && (!levelError || levelRows.length > 0)">{{ t('level.board') }}</h2>
    <ol v-if="levelRows.length > 0 || showLevelYou" class="rank-board">
      <li
        v-for="(row, i) in levelRows"
        :key="row.username + i"
        :class="{ me: isLevelMe(row) }"
        :aria-current="isLevelMe(row) ? 'true' : undefined"
      >
        <span class="rank-pos">{{ placeAt('level', i) }}</span>
        <AvatarView
          :config="portraitFor(row.username, row.avatar)"
          :size="48"
        />
        <span class="level-badge">{{ row.level }}</span>
        <div class="rank-who">
          <router-link
            class="rank-user"
            :to="publicProfilePath(row.username)"
          >{{ row.username }}</router-link>
          <span v-if="isLevelMe(row)" class="you-chip">{{ t('rank.you') }}</span>
        </div>
        <span class="rank-meta">{{ levelBarText(row) }}</span>
      </li>
      <li
        v-if="showLevelYou && levelYou"
        class="me off-board"
        aria-current="true"
      >
        <span class="rank-pos">{{ levelYou.position }}</span>
        <AvatarView :config="player.avatar" :size="48" />
        <span class="level-badge">{{ levelYou.level }}</span>
        <div class="rank-who">
          <router-link
            class="rank-user"
            :to="publicProfilePath(player.username)"
          >{{ player.username }}</router-link>
          <span class="you-chip">{{ t('rank.you') }}</span>
        </div>
        <span class="rank-meta">{{ levelBarText(levelYou) }}</span>
      </li>
    </ol>
    <p v-else-if="!loading && !levelError" class="hint">
      {{ t('level.boardEmpty') }}
    </p>
    </template>

    <template v-else-if="pane === 'event'">
    <section v-if="eventBoard" class="md-panel hero">
      <div class="hero-row">
        <span class="win-badge win-badge-lg" aria-hidden="true">{{ eventYouWins }}</span>
        <div class="hero-copy">
          <p class="hero-name">{{ t(`eventQueue.modes.${eventBoard.mode}`) }}</p>
          <p class="hero-lp">
            {{
              eventBoard.status === 'live'
                ? t('eventQueue.liveUntil', { time: eventEndsLabel })
                : t('eventQueue.closed')
            }}
          </p>
        </div>
      </div>
    </section>
    <p v-else-if="!eventError" class="hint">{{ t('eventQueue.loading') }}</p>
    <p v-else class="empty">{{ t('eventQueue.empty') }}</p>
    <h2 v-if="eventBoard">{{ t('eventQueue.board') }}</h2>
    <ol v-if="eventBoard && eventBoard.entries.length > 0" class="rank-board">
      <li
        v-for="row in eventBoard.entries"
        :key="row.userId"
        :class="{ me: isEventMe(row.username) }"
        :aria-current="isEventMe(row.username) ? 'true' : undefined"
      >
        <span class="rank-pos">{{ row.position }}</span>
        <AvatarView :config="portraitFor(row.username, row.avatar)" :size="48" />
        <span class="win-badge" aria-hidden="true">{{ row.wins }}</span>
        <div class="rank-who">
          <router-link class="rank-user" :to="publicProfilePath(row.username)">
            {{ row.username }}
          </router-link>
          <span v-if="isEventMe(row.username)" class="you-chip">{{ t('rank.you') }}</span>
        </div>
        <span class="rank-meta">{{ row.wins }}–{{ row.losses }}</span>
      </li>
    </ol>
    <p v-else-if="eventBoard && !eventError" class="hint">{{ t('eventQueue.boardEmpty') }}</p>
    </template>

    <template v-else-if="pane === 'prizes'">
    <section class="prize-panel" :aria-label="t('rank.rewardsTitle')">
      <h2>{{ t('rank.rewardsTitle') }}</h2>
      <p class="prize-lead">{{ t('rank.rewardsLead') }}</p>
      <table class="prize-table">
        <thead>
          <tr>
            <th scope="col">{{ t('rank.rewardsLeague') }}</th>
            <th scope="col">{{ t('rank.rewardsPrize') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="id in leagueIds" :key="id">
            <th scope="row">
              <span class="prize-who">
                <img :src="badgeSrc[id]" alt="" width="40" height="40" />
                <span>{{ t(`rank.leagues.${id}`) }}</span>
              </span>
            </th>
            <td>
              <span class="prize-pack">
                <img :src="packCover" alt="" />
                <span>{{ packPrize(seasonPacks[id]) }}</span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="prize-note">{{
        t('rank.rewardsClimb', {
          league: leaguePromoPacks,
          division: divisionPromoPacks,
        })
      }}</p>
    </section>

    <section class="prize-panel" :aria-label="t('aiWins.rewardTitle')">
      <h2>{{ t('aiWins.rewardTitle') }}</h2>
      <table class="prize-table">
        <thead>
          <tr>
            <th scope="col">{{ t('aiWins.rewardPlace') }}</th>
            <th scope="col">{{ t('rank.rewardsPrize') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{{ t('aiWins.rewardRange', { n: ladderRewardTop }) }}</th>
            <td>
              <span class="prize-pack">
                <img :src="packCover" alt="" />
                <span>{{ packPrize(ladderRewardPacks) }}</span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="prize-note">{{ t('aiWins.rewardNote', { n: ladderRewardTop }) }}</p>
    </section>
    </template>

    <template v-else>
    <section v-if="aiLoaded && !aiError" class="md-panel hero">
      <div class="hero-row">
        <span class="win-badge win-badge-lg" aria-hidden="true">{{ aiCurrent.you.wins }}</span>
        <div class="hero-copy">
          <p class="hero-name">{{ winsText(aiCurrent.you.wins) }}</p>
          <p v-if="aiPlace" class="hero-lp">{{ aiPlace }}</p>
        </div>
      </div>
    </section>
    <p v-else-if="!aiError" class="hint">{{ t('aiWins.loading') }}</p>

    <h2 v-if="aiLoaded && (!aiError || aiCurrent.entries.length > 0)">
      {{ t('aiWins.board') }}
    </h2>
    <ol v-if="aiCurrent.entries.length > 0 || showAiYou" class="rank-board">
      <li
        v-for="(row, i) in aiCurrent.entries"
        :key="row.username + i"
        :class="{ me: isAiMe(row) }"
        :aria-current="isAiMe(row) ? 'true' : undefined"
      >
        <span class="rank-pos">{{ placeAt(pane, i) }}</span>
        <AvatarView
          :config="portraitFor(row.username, row.avatar)"
          :size="48"
        />
        <span class="win-badge" aria-hidden="true">{{ row.wins }}</span>
        <div class="rank-who">
          <router-link
            class="rank-user"
            :to="publicProfilePath(row.username)"
          >{{ row.username }}</router-link>
          <span v-if="isAiMe(row)" class="you-chip">{{ t('rank.you') }}</span>
        </div>
        <span class="rank-meta">{{ winsText(row.wins) }}</span>
      </li>
      <li
        v-if="showAiYou"
        class="me off-board"
        aria-current="true"
      >
        <span class="rank-pos">{{ aiCurrent.you.position }}</span>
        <AvatarView :config="player.avatar" :size="48" />
        <span class="win-badge" aria-hidden="true">{{ aiCurrent.you.wins }}</span>
        <div class="rank-who">
          <router-link
            class="rank-user"
            :to="publicProfilePath(player.username)"
          >{{ player.username }}</router-link>
          <span class="you-chip">{{ t('rank.you') }}</span>
        </div>
        <span class="rank-meta">{{ winsText(aiCurrent.you.wins) }}</span>
      </li>
    </ol>
    <p v-else-if="aiLoaded && !aiError" class="hint">
      {{ t('aiWins.empty', { name: pageTitle }) }}
    </p>
    </template>

    <nav
      v-if="showPager"
      class="pager"
      :aria-label="t('rank.pagerLabel')"
    >
      <button
        type="button"
        class="text-btn pager-btn"
        :disabled="loading || activeWindow.page <= 1"
        @click="setPage(activeWindow.page - 1)"
      >
        {{ t('rank.prev') }}
      </button>
      <span class="page-status">{{
        t('rank.pager', { page: activeWindow.page, pages: pageCount })
      }}</span>
      <button
        type="button"
        class="text-btn pager-btn"
        :disabled="loading || activeWindow.page >= pageCount"
        @click="setPage(activeWindow.page + 1)"
      >
        {{ t('rank.next') }}
      </button>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  AI_LADDER_REWARD_PACKS,
  AI_LADDER_REWARD_TOP,
  DIVISION_PROMO_PACKS,
  isPlacement,
  LEAGUE_BADGE_SRC,
  LEAGUE_IDS,
  LEAGUE_PROMO_PACKS,
  SEASON_END_PACKS,
  type AvatarConfig,
  type RankedStanding,
} from '@shared/index'
import RankBadge from '../components/RankBadge.vue'
import AvatarView from '../components/AvatarView.vue'
import {
  fetchLeaderboard,
  fetchMyRating,
  type LeaderboardEntry,
} from '../api/rating'
import {
  AI_WIN_LADDERS,
  fetchAiWinBoards,
  fetchLevelBoard,
  publicProfilePath,
  type AiWinBoard,
  type AiWinEntry,
  type AiWinLadder,
  type LevelBoardEntry,
  type LevelBoardYou,
} from '../api/profile'
import { fetchEventBoard } from '../api/events'
import type { EventQueueBoard } from '@shared/index'
import { useAuthStore } from '../stores/auth'
import { usePlayerStore } from '../stores/player'
import { PACK_BY_ID, SHOP_PACK_ID } from '../game/packs'
import { useI18n } from '../i18n/t'
import { intlLocale, type AppLocale } from '../stores/locale'

const { t, locale } = useI18n()
const auth = useAuthStore()
const player = usePlayerStore()
const route = useRoute()
const router = useRouter()

type BoardPane = 'ranked' | 'level' | 'prizes' | 'event' | AiWinLadder

function isAiPane(value: BoardPane): value is AiWinLadder {
  return value === 'hard' || value === 'expert' || value === 'master'
}

const pane = computed((): BoardPane => {
  const raw = route.query.board
  if (
    raw === 'level' ||
    raw === 'prizes' ||
    raw === 'event' ||
    raw === 'hard' ||
    raw === 'expert' ||
    raw === 'master'
  ) {
    return raw
  }
  return 'ranked'
})

const leagueIds = LEAGUE_IDS
const badgeSrc = LEAGUE_BADGE_SRC
const seasonPacks = SEASON_END_PACKS
const leaguePromoPacks = LEAGUE_PROMO_PACKS
const divisionPromoPacks = DIVISION_PROMO_PACKS
const ladderRewardTop = AI_LADDER_REWARD_TOP
const ladderRewardPacks = AI_LADDER_REWARD_PACKS
const packCover =
  PACK_BY_ID[SHOP_PACK_ID]?.cover ?? '/assets/packs/pack-master.webp'

function packPrize(n: number): string {
  return n === 1 ? t('rank.rewardPack') : t('rank.rewardPacks', { n })
}

const standing = ref<RankedStanding | null>(null)
const board = ref<LeaderboardEntry[]>([])
const youPosition = ref<number | null>(null)
const seasonStartedAt = ref<string | null>(null)
const levelRows = ref<LevelBoardEntry[]>([])
const levelYou = ref<LevelBoardYou | null>(null)
function emptyAiBoard(): AiWinBoard {
  return {
    entries: [],
    total: 0,
    page: 1,
    pageSize: 50,
    you: { position: null, wins: 0 },
  }
}

const aiBoards = ref<Record<AiWinLadder, AiWinBoard>>({
  hard: emptyAiBoard(),
  expert: emptyAiBoard(),
  master: emptyAiBoard(),
})

type LadderWindow = { page: number; pageSize: number; total: number }

const windows = ref<Record<string, LadderWindow>>({})
const aiLoaded = ref(false)
const loading = ref(true)
const loadError = ref(false)
const levelError = ref(false)
const aiError = ref(false)
const eventBoard = ref<EventQueueBoard | null>(null)
const eventError = ref(false)

const pageKicker = computed(() => {
  if (pane.value === 'ranked' || pane.value === 'prizes') return t('rank.kicker')
  if (pane.value === 'level') return t('level.kicker')
  if (pane.value === 'event') return t('eventQueue.kicker')
  return t('aiWins.kicker')
})
const pageTitle = computed(() => {
  if (pane.value === 'ranked') return t('rank.title')
  if (pane.value === 'level') return t('rank.tabs.level')
  if (pane.value === 'prizes') return t('rank.tabs.prizes')
  if (pane.value === 'event') return t('rank.tabs.event')
  return t(`settings.vsAiLevels.${pane.value}`)
})
const pageLead = computed(() => {
  if (pane.value === 'ranked') return t('rank.lead')
  if (pane.value === 'level') return t('level.lead')
  if (pane.value === 'prizes') return t('rank.prizesLead')
  if (pane.value === 'event') return t('eventQueue.lead')
  return t('aiWins.lead')
})
const activeError = computed(() => {
  if (pane.value === 'prizes') return false
  if (pane.value === 'ranked') return loadError.value
  if (pane.value === 'level') return levelError.value
  if (pane.value === 'event') return eventError.value
  return aiError.value
})
const eventYouWins = computed(() => eventBoard.value?.you?.wins ?? 0)
const eventEndsLabel = computed(() => {
  const iso = eventBoard.value?.endsAt
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString()
})
function isEventMe(username: string): boolean {
  return username === player.username
}

const standingLabel = computed(() => labelFor(standing.value))

const standingLp = computed(() => {
  const s = standing.value
  if (!s || isPlacement(s) || !s.league) return ''
  if (s.league === 'king') return t('rank.lpBare', { lp: s.lp })
  return t('rank.lpCount', { lp: s.lp })
})

const showLpBar = computed(() => {
  const s = standing.value
  return Boolean(s && !isPlacement(s) && s.league && s.league !== 'king')
})

const lpPct = computed(() => {
  const s = standing.value
  if (!s) return 0
  return Math.max(0, Math.min(100, s.lp))
})

const seasonLabel = computed(() => {
  const raw = seasonStartedAt.value
  if (!raw) return ''
  const date = formatSeasonDate(raw, locale.value)
  return date ? t('rank.seasonSince', { date }) : ''
})

const inBoard = computed(() =>
  board.value.some((row) => row.username === player.username),
)

const youEntry = computed((): LeaderboardEntry | null => {
  const s = standing.value
  if (!s) return null
  return {
    username: player.username,
    avatar: player.avatar,
    league: s.league,
    division: s.division,
    lp: s.lp,
    placementRemaining: s.placementRemaining,
  }
})

const showYouRow = computed(() => {
  const s = standing.value
  if (!s || isPlacement(s) || youPosition.value == null) return false
  return !inBoard.value
})

/**
 * League name plus roman division, or the placement line.
 */
function labelFor(s: RankedStanding | null): string {
  if (!s) return ''
  if (isPlacement(s) || !s.league) {
    return t('rank.placementLeft', { n: s.placementRemaining })
  }
  const name = t(`rank.leagues.${s.league}`)
  if (!s.division || s.league === 'king') return name
  return `${name} ${s.division === 1 ? 'I' : 'II'}`
}

function boardLabel(row: LeaderboardEntry): string {
  if (!row.league) return t('rank.placement')
  const name = t(`rank.leagues.${row.league}`)
  const div =
    row.division && row.league !== 'king'
      ? ` ${row.division === 1 ? 'I' : 'II'}`
      : ''
  return `${name}${div} · ${t('rank.lpBare', { lp: row.lp })}`
}

function isMe(row: LeaderboardEntry): boolean {
  return row.username === player.username
}

function portraitFor(username: string, avatar: AvatarConfig): AvatarConfig {
  return username === player.username ? player.avatar : avatar
}

const levelPct = computed(() => {
  const row = levelYou.value
  if (!row || row.xpToNext <= 0) return 100
  return Math.max(0, Math.min(100, (row.xpIntoLevel / row.xpToNext) * 100))
})

const levelPlace = computed(() => {
  const pos = levelYou.value?.position
  if (pos == null) return ''
  return t('level.place', { n: pos })
})

const levelInBoard = computed(() =>
  levelRows.value.some((row) => row.username === player.username),
)

const showLevelYou = computed(() => {
  const you = levelYou.value
  if (!you || you.position == null) return false
  return !levelInBoard.value
})

function levelBarText(row: {
  level: number
  xpIntoLevel: number
  xpToNext: number
}): string {
  return t('level.bar', { into: row.xpIntoLevel, need: row.xpToNext })
}

function isLevelMe(row: LevelBoardEntry): boolean {
  return row.username === player.username
}

function setPane(next: BoardPane): void {
  const query = { ...route.query }
  if (next === 'ranked') delete query.board
  else query.board = next
  delete query.page
  void router.replace({ query })
}

const requestedPage = computed(() => {
  const raw = Number(route.query.page)
  if (!Number.isFinite(raw) || raw < 1) return 1
  return Math.min(10_000, Math.floor(raw))
})

function windowFor(id: string): LadderWindow {
  return windows.value[id] ?? { page: 1, pageSize: 50, total: 0 }
}

function rememberWindow(
  id: string,
  window: LadderWindow,
): void {
  windows.value = { ...windows.value, [id]: window }
}

function placeAt(id: string, index: number): number {
  const ladder = windowFor(id)
  return (ladder.page - 1) * ladder.pageSize + index + 1
}

const activeWindow = computed(() => windowFor(pane.value))

const pageCount = computed(() => {
  const ladder = activeWindow.value
  if (ladder.pageSize < 1) return 1
  return Math.max(1, Math.ceil(ladder.total / ladder.pageSize))
})

const showPager = computed(
  () => pageCount.value > 1 && !activeError.value,
)

function setPage(next: number): void {
  if (next < 1 || next > pageCount.value || next === activeWindow.value.page) {
    return
  }
  const query = { ...route.query }
  if (next <= 1) delete query.page
  else query.page = String(next)
  void router.replace({ query })
}

const aiCurrent = computed((): AiWinBoard => {
  if (!isAiPane(pane.value)) return emptyAiBoard()
  return aiBoards.value[pane.value]
})

const aiPlace = computed(() => {
  const pos = aiCurrent.value.you.position
  if (pos == null) return ''
  return t('aiWins.place', { n: pos })
})

const aiInBoard = computed(() =>
  aiCurrent.value.entries.some((row) => row.username === player.username),
)

const showAiYou = computed(() => {
  const you = aiCurrent.value.you
  if (you.position == null) return false
  return !aiInBoard.value
})

function winsText(wins: number): string {
  if (wins === 1) return t('aiWins.one')
  return t('aiWins.many', { n: wins })
}

function isAiMe(row: AiWinEntry): boolean {
  return row.username === player.username
}

function formatSeasonDate(iso: string, loc: AppLocale): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(intlLocale(loc), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function adoptServerPage(serverPage: number): void {
  if (serverPage === requestedPage.value) return
  const query = { ...route.query }
  if (serverPage <= 1) delete query.page
  else query.page = String(serverPage)
  void router.replace({ query })
}

let loadGen = 0

async function load(): Promise<void> {
  const token = auth.token
  const which = pane.value
  const requested = requestedPage.value
  const gen = ++loadGen
  if (!token) {
    loadError.value = true
    levelError.value = true
    aiError.value = true
    eventError.value = true
    loading.value = false
    return
  }
  if (which === 'prizes') {
    loading.value = false
    return
  }
  loading.value = true
  if (which === 'ranked') loadError.value = false
  else if (which === 'level') levelError.value = false
  else if (which === 'event') eventError.value = false
  else aiError.value = false
  const still = () =>
    gen === loadGen && pane.value === which && requestedPage.value === requested
  if (which === 'ranked') {
    const [me, ladder] = await Promise.allSettled([
      fetchMyRating(token),
      fetchLeaderboard(token, requested),
    ])
    if (!still()) {
      if (gen === loadGen) loading.value = false
      return
    }
    if (me.status === 'fulfilled') {
      standing.value = me.value.standing
      if (!seasonStartedAt.value) {
        seasonStartedAt.value = me.value.seasonStartedAt
      }
    }
    if (ladder.status === 'fulfilled') {
      board.value = ladder.value.entries
      seasonStartedAt.value =
        ladder.value.seasonStartedAt ?? seasonStartedAt.value
      youPosition.value = ladder.value.you?.position ?? null
      rememberWindow('ranked', ladder.value)
      adoptServerPage(ladder.value.page)
    } else {
      loadError.value = true
    }
  } else if (which === 'level') {
    const levels = await fetchLevelBoard(token, requested).then(
      (value) => ({ ok: true as const, value }),
      () => ({ ok: false as const }),
    )
    if (!still()) {
      if (gen === loadGen) loading.value = false
      return
    }
    if (levels.ok) {
      levelRows.value = levels.value.entries
      levelYou.value = levels.value.you
      rememberWindow('level', levels.value)
      adoptServerPage(levels.value.page)
    } else {
      levelError.value = true
    }
  } else if (which === 'event') {
    const body = await fetchEventBoard(token).then(
      (value) => ({ ok: true as const, value }),
      () => ({ ok: false as const }),
    )
    if (!still()) {
      if (gen === loadGen) loading.value = false
      return
    }
    if (body.ok && 'mode' in body.value) {
      eventBoard.value = body.value
    } else if (body.ok) {
      eventBoard.value = null
    } else {
      eventError.value = true
    }
  } else {
    const wins = await fetchAiWinBoards(token, requested).then(
      (value) => ({ ok: true as const, value }),
      () => ({ ok: false as const }),
    )
    if (!still()) {
      if (gen === loadGen) loading.value = false
      return
    }
    if (wins.ok) {
      aiBoards.value = wins.value
      aiLoaded.value = true
      for (const id of AI_WIN_LADDERS) rememberWindow(id, wins.value[id])
      adoptServerPage(wins.value[which].page)
    } else {
      aiError.value = true
    }
  }
  if (gen === loadGen) loading.value = false
}

function onRetry(): void {
  void load()
}

watch(
  () => [pane.value, requestedPage.value] as const,
  () => {
    void load()
  },
  { immediate: true },
)
</script>

<style scoped>
.rank-page {
  width: min(960px, 100%);
  margin: 0 auto;
  padding: 32px 16px 48px;
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.support,
.season,
.hint,
.empty {
  margin: 8px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.season {
  margin-top: 4px;
  font-size: 13px;
}

.hint,
.empty {
  margin: 0 0 16px;
}

.text-btn {
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}

.retry {
  display: inline;
  margin-left: 8px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.md-panel {
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.hero-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-name {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.hero-lp {
  margin: 4px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.lp-bar {
  width: min(240px, 100%);
  height: 10px;
  margin-top: 10px;
  border-radius: 999px;
  background: var(--md-sys-color-outline-variant);
  overflow: hidden;
}

.lp-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--md-sys-color-primary);
  transition: width var(--md-motion-medium) var(--md-motion-emphasized);
}

.md-btn-tonal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  text-decoration: none;
  cursor: pointer;
}

.play {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.prize-panel {
  margin: 0 0 24px;
}

.prize-panel h2 {
  margin-bottom: 4px;
}

.prize-lead,
.prize-note {
  margin: 0 0 12px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.prize-note {
  margin: 10px 0 0;
}

.prize-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--md-sys-color-surface-container);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--md-elevation-1);
}

.prize-table th,
.prize-table td {
  padding: 10px 16px;
  text-align: left;
  vertical-align: middle;
}

.prize-table thead th {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.prize-table tbody th {
  font-size: 14px;
  font-weight: 600;
}

.prize-table tbody tr + tr th,
.prize-table tbody tr + tr td {
  border-top: 1px solid var(--md-sys-color-outline-variant);
}

.prize-who,
.prize-pack {
  display: flex;
  align-items: center;
  gap: 10px;
}

.prize-who img {
  display: block;
  border-radius: 50%;
  object-fit: cover;
}

.prize-pack img {
  display: block;
  width: 36px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 3px;
}

.pane-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 0 16px;
}

.pane-tab {
  min-height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.pane-tab.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.level-badge {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.level-badge-lg {
  width: 96px;
  height: 96px;
  font-size: 32px;
}

.rank-board li.me .level-badge,
.rank-board li.me .win-badge {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.win-badge {
  display: grid;
  place-items: center;
  min-width: 40px;
  height: 40px;
  padding: 0 8px;
  box-sizing: border-box;
  border-radius: 20px;
  background: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.win-badge-lg {
  min-width: 96px;
  height: 96px;
  padding: 0 16px;
  border-radius: 48px;
  font-size: 28px;
}

h2 {
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 400;
}

.rank-board {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.rank-board li {
  display: grid;
  grid-template-columns: minmax(2rem, max-content) 48px auto 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
}

.rank-board li.me {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.rank-board li.off-board {
  margin-top: 8px;
}

.rank-pos {
  min-width: 2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: end;
  color: var(--md-sys-color-on-surface-variant);
}

.rank-board li.me .rank-pos,
.rank-board li.me .rank-meta {
  color: inherit;
}

.rank-who {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.rank-user {
  font-weight: 600;
  color: inherit;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-user:hover {
  color: var(--md-sys-color-primary);
}

.rank-board li.me .rank-user:hover {
  color: var(--md-sys-color-on-primary-container);
  text-decoration: underline;
}

.you-chip {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.rank-meta {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  white-space: nowrap;
}

.pager {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.pager-btn {
  min-height: 40px;
  padding: 0 4px;
  border: 0;
  background: none;
  cursor: pointer;
}

.pager-btn:disabled {
  opacity: 0.38;
  cursor: default;
}

.page-status {
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--md-sys-color-on-surface-variant);
}

@media (prefers-reduced-motion: reduce) {
  .lp-fill {
    transition: none;
  }
}

@media (max-width: 640px) {
  h1 {
    font-size: 28px;
    line-height: 36px;
  }

  .page-header {
    flex-direction: column;
  }

  .rank-board li {
    grid-template-columns: minmax(2rem, max-content) 48px auto 1fr;
  }

  .rank-meta {
    grid-column: 4;
    justify-self: start;
  }
}
</style>
